import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../config';
import '../styles/components/profileTabs.css';

interface RecentJob {
  id: number;
  user_id: number;
  status: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number;
  processing_time_ms?: number;
  total_tokens?: number;
  cost_usd?: number;
}

interface AIMetrics {
  avg_processing_time_ms: number;
  avg_tokens_per_scan: number;
  avg_cost_per_scan: number;
  total_tokens: number;
  total_cost_usd: number;
  completed_scans: number;
}

interface QueueStats {
  total_jobs: number;
  pending_jobs: number;
  processing_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  avg_processing_time_seconds: number;
  oldest_pending_job_age_seconds: number;
  ai_metrics?: AIMetrics;
  recent_jobs: RecentJob[];
}

const QueueTab: React.FC = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/queue/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        } else {
          setError('Failed to load queue statistics');
        }
      } catch (err) {
        console.error('Failed to fetch queue stats:', err);
        setError('Failed to load queue statistics');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
    
    // Refresh every 5 seconds
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, [token]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed': return 'var(--color-success)';
      case 'processing': return 'var(--color-accent-secondary)';
      case 'pending': return 'var(--color-warning)';
      case 'failed': return 'var(--color-danger)';
      default: return 'var(--color-text-muted)';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {loading && !stats ? (
        <div className="stats-loading">Loading queue statistics...</div>
      ) : error ? (
        <div className="stats-error">{error}</div>
      ) : stats ? (
        <>
          {/* Queue Status - Compact Multi-line */}
          <div style={{ 
            background: 'var(--color-bg-primary)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            margin: 0
          }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                padding: '16px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
                  <span className="stat-compact-value">{stats.total_jobs.toLocaleString()}</span>
                  <span className="stat-compact-label">Total</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
                  <span className="stat-compact-value" style={{ color: 'var(--color-warning)' }}>{stats.pending_jobs.toLocaleString()}</span>
                  <span className="stat-compact-label">Pending</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
                  <span className="stat-compact-value" style={{ color: 'var(--color-accent-secondary)' }}>{stats.processing_jobs.toLocaleString()}</span>
                  <span className="stat-compact-label">Processing</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
                  <span className="stat-compact-value" style={{ color: 'var(--color-success)' }}>{stats.completed_jobs.toLocaleString()}</span>
                  <span className="stat-compact-label">Completed</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
                  <span className="stat-compact-value" style={{ color: 'var(--color-danger)' }}>{stats.failed_jobs.toLocaleString()}</span>
                  <span className="stat-compact-label">Failed</span>
                </div>
              </div>
          </div>

          {/* AI Metrics */}
          {stats.ai_metrics && (
            <div style={{ 
              background: 'var(--color-bg-primary)',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              margin: 0
            }}>
              <h3 style={{ margin: '16px 16px 12px 16px', fontSize: '16px' }}>💰 AI Vision Metrics</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                padding: '0 16px 16px 16px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Avg Tokens</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                    {stats.ai_metrics.avg_tokens_per_scan.toFixed(0)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Avg Cost</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                    ${stats.ai_metrics.avg_cost_per_scan.toFixed(4)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Avg Time</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                    {(stats.ai_metrics.avg_processing_time_ms / 1000).toFixed(1)}s
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Total Tokens</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                    {stats.ai_metrics.total_tokens.toLocaleString()}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Total Cost</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-success)' }}>
                    ${stats.ai_metrics.total_cost_usd.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Scans</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                    {stats.ai_metrics.completed_scans.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Recent Jobs */}
          <div style={{ 
            background: 'var(--color-bg-primary)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            margin: 0
          }}>
              <h3 style={{ margin: '16px 16px 16px 16px' }}>10 Most Recent Jobs</h3>
              <div className="stats-table-container" style={{ padding: '0 16px 16px 16px' }}>
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Duration</th>
                      <th>Tokens</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_jobs.map((job) => (
                      <tr key={job.id}>
                        <td>#{job.id}</td>
                        <td>
                          <span style={{ 
                            color: getStatusColor(job.status),
                            fontWeight: 600,
                            textTransform: 'capitalize'
                          }}>
                            {job.status}
                          </span>
                        </td>
                        <td>{formatDateTime(job.created_at)}</td>
                        <td>{formatDuration(job.duration_seconds)}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                          {job.total_tokens ? job.total_tokens.toLocaleString() : '-'}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--color-success)' }}>
                          {job.cost_usd ? `$${job.cost_usd.toFixed(4)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '12px', margin: '12px 16px 16px 16px' }}>
                📡 Auto-refreshing every 5 seconds
              </p>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default QueueTab;
